(function(){
  'use strict';
  const SEL          = 'tgcGiftSel';
  const GIFT_MARKER  = '_tgc_gift';
  // DISCOUNT_CODE removed — free gift pricing is handled by a Shopify Automatic
  // Discount (100% off, Free Products collection, $100 min). No code needed.
  const COL_CACHE_PFX = 'tgcCol_v4_';
  const COL_CACHE_TTL = 30 * 60 * 1000;

  if (!window.TGC_CONFIG?.tiers?.length) return;

  const TIERS = window.TGC_CONFIG.tiers.sort((a,b) => a.threshold - b.threshold);
  let tierProducts     = {};
  let reconcileTimer   = null;
  let isReconciling    = false;
  let pendingReconcile = false;
  let tgcValidating    = false;
  let firstRenderDone  = false;
  let isLoading        = true;
  let cachedCart        = null;  // always holds latest confirmed cart
  let mutationPending   = false; // true when interceptor detected a real cart change
  let reconcileFailures = 0;     // consecutive error count — drives backoff delay
  let _blockTimer       = null;  // safety-net timer that force-unblocks after 5 s

  /* ─── isGift: robust against null / array-format / boolean properties ── */
  // Matches string 'true', boolean true, and falls back to _tierNum presence
  // in case Shopify drops _tgc_gift but retains _tierNum across a cart merge.
  const isGift = it => {
    if (!it?.properties) return false;
    const p = it.properties;
    if (Array.isArray(p)) {
      const marker = p.find(e => e.name === GIFT_MARKER);
      const tierN  = p.find(e => e.name === '_tierNum');
      return (marker && marker.value !== 'false') || Boolean(tierN?.value);
    }
    return (Boolean(p[GIFT_MARKER]) && p[GIFT_MARKER] !== 'false') || Boolean(p._tierNum);
  };

  /* ─── Checkout blocking — the core safety mechanism ─────────────────── */
  // Block checkout the INSTANT any cart mutation is detected (before API round-trips).
  // Only unblock after reconcile confirms the cart is in a valid state.
  // Saves original button text so "Calculating Free Gift..." can be swapped in/out.
  function setCheckoutBlocked(blocked) {
    clearTimeout(_blockTimer);
    document.querySelectorAll('button[name="checkout"]').forEach(btn => {
      if (blocked) {
        if (!btn.dataset.tgcOrig) btn.dataset.tgcOrig = btn.textContent.trim();
        btn.textContent   = 'Calculating Free Gift...';
        btn.disabled      = true;
        btn.style.opacity = '0.7';
        btn.style.cursor  = 'not-allowed';
      } else {
        if (btn.dataset.tgcOrig) { btn.textContent = btn.dataset.tgcOrig; delete btn.dataset.tgcOrig; }
        btn.disabled      = false;
        btn.style.opacity = '';
        btn.style.cursor  = '';
      }
    });
    // Safety net: if a 429, network error, or any bug prevents reconcile from
    // finishing, never leave checkout blocked for more than 5 seconds.
    if (blocked) _blockTimer = setTimeout(() => setCheckoutBlocked(false), 5000);
  }

  /* ─── Intercept fetch — block checkout BEFORE the await ─────────────── */
  const _fetch = window.fetch;
  window.fetch = async function(...args) {
    const url      = (typeof args[0] === 'string' ? args[0] : args[0]?.url) || '';
    const isTgcCall = args[1]?.headers?.['X-TGC'] === '1';

    // Block free-tier products from being added to cart via fetch
    if (!isTgcCall && /\/cart\/add/.test(url) && freeTierVariantIds.size) {
      try {
        const body = args[1]?.body;
        const parsed = typeof body === 'string' ? JSON.parse(body) : null;
        const items = parsed?.items || (parsed?.id ? [parsed] : []);
        const blocked = items.some(item => freeTierVariantIds.has(String(item.id || item.variant_id)));
        if (blocked) {
          showToast('❌ This product is not available for purchase.');
          return new Response(JSON.stringify({ status: 422, message: 'Not available' }), { status: 422, headers: { 'Content-Type': 'application/json' } });
        }
      } catch(_) {}
    }

    // Strip free-tier products from predictive search results
    if (/\/search\/suggest/.test(url) && freeTierHandles.size) {
      const result = await _fetch.apply(this, args);
      try {
        const clone = result.clone();
        const data  = await clone.json();
        if (data?.resources?.results?.products) {
          data.resources.results.products = data.resources.results.products.filter(p => !freeTierHandles.has(p.handle));
          return new Response(JSON.stringify(data), { status: result.status, headers: { 'Content-Type': 'application/json' } });
        }
      } catch(_) {}
      return result;
    }

    if (!isTgcCall && /\/cart\/(add|change|update)/.test(url)) {
      mutationPending = true;
      setCheckoutBlocked(true); // instant — before the network call even starts
    }

    const result = await _fetch.apply(this, args);

    if (!isTgcCall && /\/cart\/(add|change|update)/.test(url)) {
      // For change/update, the response body IS the updated cart — use it to:
      // 1. Optimistically update the TGC UI immediately (hides gift section instantly if
      //    the subtotal no longer qualifies, without waiting for reconcile's network calls).
      // 2. Pre-populate cachedCart so reconcile skips its redundant first fetchCart().
      if (/\/cart\/(change|update)/.test(url)) {
        try {
          const snap = await result.clone().json();
          if (snap?.items && snap?.token) {
            cachedCart = snap;
            updateUI(snap);         // instant — gift section hidden/shown before reconcile starts
            if (!isReconciling) reconcile(snap);
            else pendingReconcile = true;
            return result;
          }
        } catch(_) {}
      }

      // For add, the response is the added line item(s), not the full cart —
      // merge it into cachedCart so the progress bar / gift reveal repaint
      // instantly without waiting for reconcile's own /cart.js round-trip.
      // Display-only: no gift line is added here and checkout stays blocked
      // (set above) until reconcile() actually confirms/adds the real gift.
      if (/\/cart\/add/.test(url) && cachedCart) {
        try {
          const addedRaw = await result.clone().json();
          const addedItems = Array.isArray(addedRaw?.items) ? addedRaw.items
            : (addedRaw?.id ? [addedRaw] : []);
          if (addedItems.length) {
            const merged = { ...cachedCart, items: cachedCart.items.map(it => ({...it})) };
            addedItems.forEach(added => {
              const existing = merged.items.find(it =>
                String(it.variant_id) === String(added.variant_id ?? added.id) &&
                JSON.stringify(it.properties || {}) === JSON.stringify(added.properties || {}));
              if (existing) {
                existing.quantity += added.quantity || 1;
                existing.final_line_price = (existing.final_line_price || 0) + (added.final_line_price || added.line_price || 0);
              } else {
                merged.items.push(added);
              }
            });
            cachedCart = merged;
            updateUI(merged); // instant — zero extra round-trips
            if (!isReconciling) reconcile(merged);
            else pendingReconcile = true;
            return result;
          }
        } catch(_) {}
      }

      scheduleReconcile(0);
    }
    return result;
  };

  /* ─── Intercept XHR — many themes use jQuery $.ajax / $.post ────────── */
  (function() {
    const _open = XMLHttpRequest.prototype.open;
    const _send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._tgcUrl = url;
      return _open.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function(...args) {
      if (this._tgcUrl && /\/cart\/add/.test(this._tgcUrl) && freeTierVariantIds.size) {
        try {
          const body = args[0];
          const parsed = typeof body === 'string' ? JSON.parse(body) : null;
          const items = parsed?.items || (parsed?.id ? [parsed] : []);
          const blocked = items.some(item => freeTierVariantIds.has(String(item.id || item.variant_id)));
          if (blocked) {
            showToast('❌ This product is not available for purchase.');
            this.abort();
            return;
          }
        } catch(_) {}
      }
      if (this._tgcUrl && /\/cart\/(add|change|update)/.test(this._tgcUrl)) {
        mutationPending = true;
        setCheckoutBlocked(true); // instant
        this.addEventListener('load', () => scheduleReconcile(0));
      }
      return _send.apply(this, args);
    };
  })();

  /* ─── Reconcile scheduler ────────────────────────────────────────────── */
  function scheduleReconcile(delay = 120) {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(() => reconcile(), delay);
  }

  /* ─── Free-tier variant + handle sets (built after loadCollections) ─── */
  let freeTierVariantIds = new Set();
  let freeTierHandles    = new Set();

  function buildFreeTierVariantSet() {
    freeTierVariantIds = new Set();
    freeTierHandles    = new Set();
    for (const tier of TIERS) {
      for (const product of (tierProducts[tier.tier] || [])) {
        freeTierHandles.add(product.handle);
        for (const v of (product.variants || [])) freeTierVariantIds.add(String(v.id));
      }
    }
  }

  /* ─── Parallel collection loading with sessionStorage cache ─────────── */
  async function loadCollections() {
    const uncached = [];
    for (const tier of TIERS) {
      try {
        const raw = sessionStorage.getItem(COL_CACHE_PFX + tier.collectionId);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Date.now() - parsed.ts < COL_CACHE_TTL) {
            tierProducts[tier.tier] = parsed.products;
            continue;
          }
        }
      } catch(_) {}
      uncached.push(tier);
    }
    if (!uncached.length) return;
    await Promise.all(uncached.map(async tier => {
      try {
        const res  = await _fetch(`/collections/${tier.collectionId}/products.json?limit=250`);
        const data = await res.json();
        tierProducts[tier.tier] = data.products || [];
        try {
          sessionStorage.setItem(COL_CACHE_PFX + tier.collectionId,
            JSON.stringify({ ts: Date.now(), products: tierProducts[tier.tier] }));
        } catch(_) {}
      } catch(e) {
        tierProducts[tier.tier] = [];
      }
    }));
  }

  /* ─── Image helpers ──────────────────────────────────────────────────── */
  function normalizeImg(src) {
    if (!src || typeof src !== 'string') return null;
    if (src.startsWith('//')) return 'https:' + src;
    if (src.startsWith('/'))  return location.origin + src;
    return src;
  }
  function getImg(product) {
    if (!product) return null;
    const fi = product.featured_image;
    if (fi) { const s = normalizeImg(typeof fi === 'string' ? fi : fi.src); if (s) return s; }
    if (product.images?.length) { const s = normalizeImg(typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src); if (s) return s; }
    return null;
  }

  /* ─── localStorage selection helpers ────────────────────────────────── */
  const loadSel = () => { try { return JSON.parse(localStorage.getItem(SEL)) || {}; } catch(_) { return {}; } };
  const saveSel = s  => { try { localStorage.setItem(SEL, JSON.stringify(s)); } catch(_) {} };

  const getSelVariant = tier => {
    const sel = loadSel(), products = tierProducts[tier.tier] || [], vid = sel[tier.tier];
    if (vid && products.some(p => p.variants.some(v => v.id == vid))) return vid;
    return products[0]?.variants[0]?.id || null;
  };
  const getSelProduct = tier => {
    const vid = getSelVariant(tier), products = tierProducts[tier.tier] || [];
    for (const p of products) for (const v of p.variants) if (v.id == vid) return { product: p, variant: v };
    return null;
  };

  /* ─── Cart API helpers ───────────────────────────────────────────────── */
  const tgcHdrs = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-TGC': '1' };

  async function fetchCart() {
    const res = await _fetch('/cart.js', { credentials: 'same-origin', headers: { 'X-TGC': '1' } });
    if (!res.ok) {
      // 429 Too Many Requests (or other server error): fall back to the last known
      // good cart rather than crashing reconcile with a JSON parse error.
      // The cached cart is accurate enough for validation; a fresh fetch will
      // succeed once Shopify's rate-limit window resets.
      if (cachedCart) return cachedCart;
      throw new Error(`/cart.js responded ${res.status}`);
    }
    const data = await res.json();
    cachedCart = data;
    return data;
  }

  async function bulkRemove(lines) {
    if (!lines.length) return;
    const updates = {};
    // Use line item KEY (not variant_id) so we remove the specific line item,
    // not all cart items sharing that variant. This prevents Tier 2's gift from
    // being wiped when Tier 1 changes to a different variant of the same product.
    lines.forEach(l => { updates[l.key] = 0; });
    await _fetch('/cart/update.js', { method:'POST', credentials:'same-origin', headers:tgcHdrs, body:JSON.stringify({ updates }) });
  }

  async function addLine(id, qty, props) {
    await _fetch('/cart/add.js', { method:'POST', credentials:'same-origin', headers:tgcHdrs, body:JSON.stringify({ id, quantity:qty, properties:props }) });
  }

  /* ─── Core reconcile ─────────────────────────────────────────────────── */
  // preloadedCart: pass an already-fetched cart to skip the extra /cart.js call
  // (used by init to avoid fetching the cart twice).
  async function reconcile(preloadedCart = null) {
    if (isReconciling) { pendingReconcile = true; return; }
    isReconciling    = true;
    pendingReconcile = false;
    // Consume the mutation flag — if this reconcile was triggered by a real cart
    // change the interceptor already blocked checkout. Safety-poll reconciles do NOT
    // block at start; they only block if they actually find work to do. This prevents
    // the 10-second block/unblock flicker when the cart hasn't changed.
    const wasMutation = mutationPending;
    mutationPending   = false;

    try {
      if (preloadedCart) cachedCart = preloadedCart;
      let cart = preloadedCart || await fetchCart();

      // Rogue guard: remove any free-tier product that was manually added via API
      // (lacks _tgc_gift marker but matches a known free-tier variant ID).
      if (freeTierVariantIds.size) {
        const rogueLines = cart.items.filter(it => !isGift(it) && freeTierVariantIds.has(String(it.variant_id)));
        if (rogueLines.length) {
          await bulkRemove(rogueLines);
          cart = await fetchCart();
        }
      }

      const nonGift  = cart.items.filter(it => !isGift(it));
      const subtotal = nonGift.reduce((s, it) => s + it.final_line_price, 0) / 100;
      const giftLines = cart.items.filter(isGift);

      // What gifts should exist based on current subtotal.
      // Keyed by tierNum (not variant_id) so two tiers can both select the same
      // product/variant without one entry overwriting the other in the Map.
      const desired = new Map(); // tierNum → variantId string
      for (const tier of TIERS) {
        if (subtotal >= tier.threshold) {
          const vid = getSelVariant(tier);
          if (vid) desired.set(tier.tier, String(vid));
        }
      }

      // Tier-aware diff: match each gift line to its tier via _tierNum property.
      // This ensures changing Tier 1's gift never touches Tier 2's gift line.
      const toRemove = [];
      const coveredTiers = new Set(); // tiers already satisfied by a correct gift line

      for (const giftLine of giftLines) {
        const tierNum    = giftLine.properties?._tierNum ? Number(giftLine.properties._tierNum) : null;
        const desiredVid = desired.get(tierNum);
        if (!desiredVid || String(giftLine.variant_id) !== desiredVid) {
          // Tier no longer qualified, selection changed, or gift has no tier tag
          toRemove.push(giftLine);
        } else if (!coveredTiers.has(tierNum)) {
          coveredTiers.add(tierNum); // first correct gift for this tier — keep it
        } else {
          toRemove.push(giftLine); // duplicate correct gift for same tier — remove extra
        }
      }

      let toAdd = [...desired.entries()]
        .filter(([tierNum]) => !coveredTiers.has(tierNum))
        .map(([tierNum, vid]) => [vid, TIERS.find(t => t.tier === tierNum)]);

      // Snapshot BEFORE giftsOutOfOrder can pad toRemove/toAdd.
      // hasRealGiftWork = gift actually needs to be added or removed (tier change).
      // giftsOutOfOrder = gift is valid but sitting in the wrong cart position (cosmetic).
      // These are treated differently: real changes block checkout; reordering runs silently.
      const hasRealGiftWork = toRemove.length > 0 || toAdd.length > 0;

      // Ensure gifts always sit at the TAIL of the cart so checkout shows them last.
      // Detect if any non-gift item appears after a gift (gifts out of position).
      let seenGift = false, giftsOutOfOrder = false;
      for (const item of cart.items) {
        if (isGift(item))  { seenGift = true; }
        else if (seenGift) { giftsOutOfOrder = true; break; }
      }
      if (giftsOutOfOrder) {
        // Move all currently-valid gifts: remove then re-add at end in tier order.
        // Use tier-aware desired map (keyed by tierNum) for correct matching.
        giftLines.forEach(g => {
          const tierNum = g.properties?._tierNum ? Number(g.properties._tierNum) : null;
          if (desired.has(tierNum) && String(g.variant_id) === desired.get(tierNum) && !toRemove.some(r => r.key === g.key))
            toRemove.push(g);
        });
        // Rebuild toAdd in ascending tier order so Tier 1 is added before Tier 2
        toAdd = TIERS
          .filter(t => desired.has(t.tier))
          .map(t => [desired.get(t.tier), t]);
      }

      if (!hasRealGiftWork && !giftsOutOfOrder) {
        // Truly nothing to do — valid cart, correct order
        updateUI(cart);
        // Only touch the button if this was a real mutation OR the cart is already
        // invalid. Safety polls on an unchanged, valid cart do NOT call
        // setCheckoutBlocked — eliminates the flicker.
        if (wasMutation || !cartIsValid(cart)) setCheckoutBlocked(!cartIsValid(cart));
        return;
      }

      // Only block checkout for real gift add/remove.
      // giftsOutOfOrder reordering is cosmetic — the right gift IS in the cart and
      // IS qualified, so there is no reason to freeze checkout during a position fix.
      if (hasRealGiftWork) setCheckoutBlocked(true);

      if (giftsOutOfOrder && toAdd.length) {
        // Sequential: remove first, then add in order — guarantees tail position
        await bulkRemove(toRemove);
        for (const [vid, tier] of toAdd)
          await addLine(vid, 1, { [GIFT_MARKER]:'true', _tierNum:String(tier.tier) });
      } else {
        // Parallel: faster path for normal add/remove
        await Promise.all([
          bulkRemove(toRemove),
          ...toAdd.map(([vid, tier]) => addLine(vid, 1, { [GIFT_MARKER]:'true', _tierNum:String(tier.tier) }))
        ]);
      }

      // Kick off the theme section refresh concurrently with the confirm fetch
      // below — they hit independent endpoints, so there's no reason to wait
      // for one before starting the other. Not awaited: refreshThemeCart()
      // swallows its own errors and must never affect the reconcile success
      // path or the reconcileFailures/backoff counter.
      if (hasRealGiftWork) refreshThemeCart();

      const fresh = await fetchCart();
      updateUI(fresh);
      // Only resolve the button if we actually blocked it, or the fresh cart is invalid
      if (hasRealGiftWork || !cartIsValid(fresh)) setCheckoutBlocked(!cartIsValid(fresh));
      reconcileFailures = 0; // clean run — reset backoff counter

    } catch(e) {
      reconcileFailures++; // increment before unblock so finally backoff uses updated count
      setCheckoutBlocked(false); // don't leave checkout blocked on error
      console.error('TGC reconcile error', e);
    } finally {
      isReconciling = false;
      updateLoadingState();
      if (pendingReconcile) {
        pendingReconcile = false;
        // Exponential backoff after failures — prevents the 429 retry storm.
        // 0 failures → 50 ms, 1 → 1 s, 2 → 2 s … capped at 15 s.
        const delay = reconcileFailures > 0 ? Math.min(1000 * reconcileFailures, 15000) : 50;
        setTimeout(() => reconcile(), delay);
      }
    }
  }

  /* ─── cartIsValid: all gift items in cart are still qualified ────────── */
  function cartIsValid(cart) {
    const nonGift  = cart.items.filter(it => !isGift(it));
    const subtotal = nonGift.reduce((s, it) => s + it.final_line_price, 0) / 100;
    return cart.items.filter(isGift).every(gift => {
      const tierNum = gift.properties?._tierNum ? Number(gift.properties._tierNum) : null;
      const tier    = TIERS.find(t => t.tier === tierNum);
      return tier && subtotal >= tier.threshold;
    });
  }

  /* ─── Loading / button state ─────────────────────────────────────────── */
  function updateLoadingState() {
    const loadingEl  = document.getElementById('tgc-gifts-loading');
    const giftsListEl = document.getElementById('tgc-gifts-list');
    if (!loadingEl || !giftsListEl) return;
    if (isLoading) {
      loadingEl.style.display  = 'block';
      giftsListEl.style.opacity       = '0.5';
      giftsListEl.style.pointerEvents = 'none';
    } else {
      loadingEl.style.display  = 'none';
      giftsListEl.style.opacity       = '1';
      giftsListEl.style.pointerEvents = 'auto';
    }
  }

  /* ─── Checkout validation on click (belt-and-suspenders) ────────────── */
  async function validateCheckout() {
    // Always use a fresh server cart for final validation — no stale data at checkout
    const cart     = await fetchCart();
    const nonGift  = cart.items.filter(it => !isGift(it));
    const subtotal = nonGift.reduce((s, it) => s + it.final_line_price, 0) / 100;
    const gifts    = cart.items.filter(isGift);
    if (!gifts.length) return { valid: true };

    const badGifts = [];
    for (const g of gifts) {
      const tierNum = g.properties?._tierNum ? Number(g.properties._tierNum) : null;
      const tier    = TIERS.find(t => t.tier === tierNum);
      if (!tier) return { valid: false, message: '⚠️ Free gift configuration invalid. Please refresh and try again.' };
      if (subtotal < tier.threshold) {
        badGifts.push({ gift: g, tier, needed: (tier.threshold - subtotal).toFixed(2) });
      }
    }

    if (!badGifts.length) return { valid: true };

    // Auto-remove disqualified gifts, then allow checkout to proceed
    try {
      const updates = {};
      badGifts.forEach(({ gift }) => { updates[gift.key] = 0; });
      await _fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
      cachedCart = null;
      return { valid: true };
    } catch (_) {
      // If auto-removal fails, block checkout and show a clear message
      const { tier, needed } = badGifts[0];
      return {
        valid: false,
        message: `❌ Your cart total dropped below the Tier ${tier.tier} minimum ($${tier.threshold.toFixed(2)}). Add $${needed} more to keep your free gift, or it will be removed on next update.`
      };
    }
  }

  /* ─── Toast ──────────────────────────────────────────────────────────── */
  function showToast(msg) {
    let c = document.getElementById('tgc-tc');
    if (!c) { c = document.createElement('div'); c.id='tgc-tc'; c.style.cssText='position:fixed;top:20px;right:20px;z-index:9999;pointer-events:none;'; document.body.appendChild(c); }
    const t = document.createElement('div');
    t.textContent = msg;
    t.style.cssText = 'background:#000;color:#fff;padding:11px 18px;border-radius:6px;margin-bottom:8px;box-shadow:0 4px 16px rgba(0,0,0,.18);font-size:13px;font-weight:500;animation:tgcIn .25s ease-out;';
    c.appendChild(t);
    setTimeout(() => { t.style.animation='tgcOut .25s ease-out forwards'; setTimeout(()=>t.remove(),250); }, 3000);
  }

  if (!document.getElementById('tgc-styles')) {
    const s = document.createElement('style'); s.id='tgc-styles';
    s.textContent = `
      @keyframes tgcIn  { from{transform:translateX(110%);opacity:0} to{transform:translateX(0);opacity:1} }
      @keyframes tgcOut { from{transform:translateX(0);opacity:1} to{transform:translateX(110%);opacity:0} }
      @keyframes tgcSpin { to{transform:rotate(360deg)} }
      .tgc-gifts-loading { display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px 16px;text-align:center; }
      .tgc-loading-spinner { width:32px;height:32px;border:3px solid rgba(0,0,0,.1);border-top-color:#000;border-radius:50%;animation:tgcSpin .8s linear infinite;margin-bottom:12px; }
      .tgc-gifts-loading p { margin:0;font-size:13px;color:#666;font-weight:500; }
    `;
    document.head.appendChild(s);
  }

  /* ─── Backdrop ───────────────────────────────────────────────────────── */
  const bd = document.createElement('div'); bd.id='tgc-cart-backdrop'; document.body.appendChild(bd);
  bd.addEventListener('click', () => document.querySelector('#nt_cart_canvas .close_pp')?.click());
  const cc = document.getElementById('nt_cart_canvas');
  if (cc) new MutationObserver(() => {
    const open = !cc.classList.contains('dn');
    bd.classList.toggle('is-active', open);
    // Reconcile every time the drawer opens so a stale cart (back-button, network
    // delay, manual item removal) is corrected before the customer sees it.
    if (open) {
      // Instant repaint from the last known cart before the network round-trip
      // resolves — purely cosmetic, reconcile() below still runs and is the
      // only thing that can add/remove gifts or unblock checkout.
      if (cachedCart) updateUI(cachedCart);
      mutationPending = true; if (!isReconciling) reconcile();
    }
  }).observe(cc, {attributes:true, attributeFilter:['class']});

  // cart:refresh is dispatched by some theme actions (e.g. page-show after back-nav).
  document.addEventListener('cart:refresh', () => { mutationPending = true; if (!isReconciling) reconcile(); });
  // pageshow fires on back-button navigation; bfcache restores the old page but the
  // real server cart may have changed (e.g. session expired, another tab removed items).
  window.addEventListener('pageshow', e => { if (e.persisted) { mutationPending = true; if (!isReconciling) reconcile(); } });

  /* ─── HTML escape ────────────────────────────────────────────────────── */
  const esc = s => String(s).replace(/[&<>"']/g, c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

  /* ─── Clean product title (strips Shopify "(FREE)" suffix) ─────────── */
  const cleanTitle = t => String(t).replace(/\s*\(FREE\)\s*$/i, '').trim();

  /* ─── TGC string helpers (reads from TGC_CONFIG.strings with fallbacks) */
  const S = window.TGC_CONFIG?.strings || {};
  const tgcStr = {
    allUnlocked : S.allUnlocked  || 'All tiers unlocked! 🎁',
    progress    : (amount, tier) => (S.progress || 'Add {amount} to your order to unlock Tier {tier} free gift').replace('{amount}', '$' + amount).replace('{tier}', tier),
    changeGift  : S.changeGift   || 'Change gift',
    unlockAt    : thresh => (S.unlockAt   || 'Unlock at ${threshold}').replace('{threshold}', thresh),
    chooseGifts : n      => (S.chooseGifts|| 'Choose 1 of {n} gifts').replace('{n}', n),
    freeBadge   : S.freeBadge    || 'Free'
  };

  /* ─── UI render ──────────────────────────────────────────────────────── */
  function updateUI(cart) {
    const nonGift  = cart.items.filter(it => !isGift(it));
    const giftLines = cart.items.filter(isGift);
    const subtotal = nonGift.reduce((s, it) => s + it.final_line_price, 0) / 100;

    const fillEl  = document.getElementById('tgc-fill');
    const msgEl   = document.getElementById('tgc-msg');
    const giftsEl = document.getElementById('tgc-gifts');
    const listEl  = document.getElementById('tgc-gifts-list');
    if (!fillEl || !msgEl || !giftsEl || !listEl) return;

    // Dots are EVENLY spaced (not value-based): the LAST tier sits at the right end
    // (100%) so the track ends on the final circle with no trailing line. With N tiers
    // dot i sits at ((i + 1) / N) * 100%, leaving the 0→first-dot gap for progress
    // toward Tier 1. Works the same whether 3, 4, or 5 tiers are enabled.
    const N = TIERS.length;
    const dotPos = TIERS.map((t, i) => ((i + 1) / N) * 100);

    // Fill passes exactly through each dot as its tier unlocks: it advances one
    // segment per reached tier, interpolating by dollar value between the two
    // surrounding thresholds so the fill edge lands on the dot at the moment of unlock.
    let reachedCount = 0;
    for (const t of TIERS) { if (subtotal >= t.threshold) reachedCount++; }
    let fillPct;
    if (reachedCount >= N) {
      fillPct = 100;
    } else {
      const startPos   = reachedCount > 0 ? dotPos[reachedCount - 1] : 0;
      const targetPos  = dotPos[reachedCount];
      const prevThresh = reachedCount > 0 ? TIERS[reachedCount - 1].threshold : 0;
      const nextThresh = TIERS[reachedCount].threshold;
      const frac = nextThresh > prevThresh
        ? Math.max(0, Math.min(1, (subtotal - prevThresh) / (nextThresh - prevThresh)))
        : 0;
      fillPct = startPos + (targetPos - startPos) * frac;
    }
    fillEl.style.width = fillPct + '%';

    const dotsEl = document.getElementById('tgc-dots');
    if (dotsEl) {
      dotsEl.innerHTML = TIERS.map((t, i) => {
        const reached = subtotal >= t.threshold;
        return `<div class="tgc-progress__dot${reached ? ' is-reached' : ''}" style="left:${dotPos[i]}%"></div>`;
      }).join('');
    }

    let msg = tgcStr.allUnlocked;
    for (const t of TIERS) { if (subtotal < t.threshold) { msg = tgcStr.progress((t.threshold-subtotal).toFixed(2), t.tier); break; } }
    msgEl.textContent = msg;
    giftsEl.hidden = TIERS.every(t => subtotal < t.threshold);

    if (!firstRenderDone) {
      firstRenderDone = true; isLoading = false;
      document.getElementById('tgc-progress')?.classList.remove('tgc-loading');
      updateLoadingState();
    }

    const openTiers = new Set();
    document.querySelectorAll('.tgc-gift[data-open]').forEach(el => openTiers.add(el.dataset.tier));

    listEl.innerHTML = TIERS.map(tier => {
      const unlocked = subtotal >= tier.threshold;
      const products = tierProducts[tier.tier] || [];
      if (!products.length) return '';
      const sel = getSelProduct(tier);
      if (!sel) return '';

      const tierBadge = tier.badge || `Tier ${tier.tier} · ${tgcStr.freeBadge}`;
      const lockSVG = `<svg class="tgc-lock-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
      if (!unlocked) return `<li class="tgc-gift tgc-gift--locked" data-tier="${tier.tier}">
        <div class="tgc-gift__row"><div class="tgc-gift__img-placeholder tgc-gift__img-placeholder--lock">${lockSVG}</div>
        <div><span class="tgc-gift__badge">Tier ${tier.tier}</span>
        <div class="tgc-gift__name">${esc(tgcStr.chooseGifts(products.length))}</div>
        <div class="tgc-gift__sub">${esc(tgcStr.unlockAt(tier.threshold))}</div></div></div></li>`;

      const liveLine   = giftLines.find(g => String(g.variant_id)===String(sel.variant.id));
      const mainImg    = normalizeImg(liveLine?.image) || getImg(sel.product);
      const opts       = products.map(p => {
        const v=p.variants[0], active=String(v.id)===String(sel.variant.id), img=getImg(p);
        return `<button type="button" class="tgc-gift__opt" data-vid="${v.id}" data-tier="${tier.tier}" aria-pressed="${active}">
          <span class="tgc-gift__opt-media">${img?`<img src="${esc(img)}" alt="${esc(cleanTitle(p.title))}">`:'<span class="tgc-gift__opt-noimg"></span>'}</span>
          <span class="tgc-gift__opt-name">${esc(cleanTitle(p.title))}</span></button>`;
      }).join('');

      return `<li class="tgc-gift" data-tier="${tier.tier}"${openTiers.has(String(tier.tier))?' data-open':''}>
        <div class="tgc-gift__row">
          ${mainImg?`<img class="tgc-gift__img" src="${esc(mainImg)}" alt="${esc(cleanTitle(sel.product.title))}">`:'<div class="tgc-gift__img-placeholder">🎁</div>'}
          <div><span class="tgc-gift__badge">${esc(tierBadge)}</span>
          <div class="tgc-gift__name">${esc(cleanTitle(sel.product.title))}</div>
          ${products.length>1?`<button type="button" class="tgc-gift__toggle" data-toggle="${tier.tier}">${esc(tgcStr.changeGift)}</button>`:''}
          </div></div>
        ${products.length>1?`<div class="tgc-gift__picker" data-picker="${tier.tier}">${opts}</div>`:''}
      </li>`;
    }).join('');

    syncCartBadge(cart);
    syncSubtotal(cart);
  }

  /* ─── Sync header cart badge ────────────────────────────────────────── */
  // TGC uses _fetch (bypasses the theme's own fetch interceptor), so the theme
  // never receives the signal to refresh its cart count badge. We do it manually.
  // Badge shows NON-GIFT quantity only — the free gift is a system-added bonus,
  // not something the customer put in the cart, so it should not inflate the count.
  /* ─── Sync theme subtotal display ───────────────────────────────────── */
  // When TGC removes a gift after a product is deleted, the theme's subtotal
  // element becomes stale. Re-sync it from the fresh cart data.
  function syncSubtotal(cart) {
    const cents = cart.items
      .filter(it => !isGift(it))
      .reduce((s, it) => s + it.final_line_price, 0);
    const fmt = typeof Shopify !== 'undefined' && Shopify.formatMoney
      ? Shopify.formatMoney(cents, window.t_moneyFormat || '${{amount}}')
      : '$' + (cents / 100).toFixed(2);
    document.querySelectorAll('.cart_tot_price').forEach(el => { el.textContent = fmt; });
  }

  function syncCartBadge(cart) {
    const qty = cart.items
      .filter(it => !isGift(it))
      .reduce((s, it) => s + it.quantity, 0);
    try { sessionStorage.setItem('tgc_badge', qty); } catch (_) {}
    applyBadge(qty);
  }

  /* ─── Refresh theme cart section after TGC mutations ────────────────── */
  // When TGC adds or removes gifts, the theme's own cart drawer DOM doesn't know
  // about those changes (TGC uses _fetch, bypassing the theme's interceptor).
  // This re-fetches the cart_js section and calls geckoShopify.onCartUpdate with
  // the fresh HTML so the theme reflects the correct empty/non-empty state and
  // correct item list and subtotal. The badge visibility trick avoids any flash
  // because setting textContent and calling onCartUpdate are synchronous.
  async function refreshThemeCart() {
    if (typeof geckoShopify?.onCartUpdate !== 'function') return;
    try {
      const res = await _fetch(location.href.split('?')[0] + '/?section_id=cart_js', {
        credentials: 'same-origin',
        headers: { 'cache-control': 'no-cache' }
      });
      const html = await res.text();
      // Force count mismatch so onCartUpdate always re-renders items + empty state.
      // Setting to '-1' and restoring visibility are both synchronous, so no flash.
      document.querySelectorAll('.jsccount').forEach(el => {
        el.style.visibility = 'hidden';
        el.textContent = '-1';
      });
      geckoShopify.onCartUpdate(0, 1, html, null);
      document.querySelectorAll('.jsccount').forEach(el => { el.style.visibility = ''; });
    } catch(_) {}
  }

  function applyBadge(qty) {
    document.querySelectorAll('.jsccount').forEach(el => {
      el.textContent = qty > 0 ? qty : '';
      el.style.display = qty > 0 ? '' : 'none';
    });
    const prev = [...document.body.classList].find(c => c.startsWith('t4_cart_count_'));
    const next = 't4_cart_count_' + qty;
    if (prev && prev !== next) document.body.classList.replace(prev, next);
    else if (!prev)            document.body.classList.add(next);
  }

  // Apply cached count immediately on load so badge is correct before first fetch
  try {
    const cached = sessionStorage.getItem('tgc_badge');
    if (cached !== null) applyBadge(Number(cached));
  } catch (_) {}

  /* ─── Checkout error banner ──────────────────────────────────────────── */
  function showCheckoutError(msg) {
    let el = document.getElementById('tgc-err');
    if (!el) {
      el = document.createElement('div'); el.id='tgc-err';
      el.style.cssText='background:#fff0f0;border:1px solid #f5c6c6;color:#c0392b;padding:12px 16px;border-radius:6px;margin-bottom:14px;font-size:13px;font-weight:500;line-height:1.4;';
      const btn = document.querySelector('button[name="checkout"]');
      btn?.parentElement?.insertBefore(el, btn);
    }
    el.textContent=msg; el.style.display='block';
  }
  function hideCheckoutError() { const el=document.getElementById('tgc-err'); if(el) el.style.display='none'; }

  /* ─── Events: picker toggle + gift selection ─────────────────────────── */
  document.addEventListener('click', async e => {
    const toggle = e.target.closest('[data-toggle]');
    if (toggle) {
      const li = toggle.closest('.tgc-gift');
      document.querySelectorAll('.tgc-gift[data-open]').forEach(el => { if(el!==li) el.removeAttribute('data-open'); });
      li.toggleAttribute('data-open');
      return;
    }
    const opt = e.target.closest('[data-vid]');
    if (opt) {
      const tierNum=Number(opt.dataset.tier), varId=opt.dataset.vid;
      const stored=loadSel(); stored[tierNum]=varId; saveSel(stored);
      const product=(tierProducts[tierNum]||[]).find(p=>p.variants.some(v=>v.id==varId));
      const li=opt.closest('.tgc-gift');
      li.removeAttribute('data-open'); li.setAttribute('data-tgc-pending','');
      try { await reconcile(); showToast(`Gift changed to ${product?.title||'Gift'}`); }
      finally { li.removeAttribute('data-tgc-pending'); }
    }
  });

  /* ─── Prevent manual gift removal ───────────────────────────────────── */
  new MutationObserver(() => {
    document.querySelectorAll('[class*="gift_item_true"]').forEach(item => {
      const rb=item.querySelector('[class*="remove"]');
      if(rb){ rb.style.pointerEvents='none'; rb.style.opacity='0.4'; }
      const lk=item.querySelector('a.mini_cart_img');
      if(lk){ lk.style.pointerEvents='none'; lk.style.cursor='default'; }
    });
  }).observe(document.body,{childList:true,subtree:true});

  /* ─── Checkout intercept (belt-and-suspenders) ───────────────────────── */
  document.addEventListener('click', async e => {
    const btn=e.target.closest('button[name="checkout"]');
    if(!btn||tgcValidating) return;
    e.preventDefault(); e.stopImmediatePropagation();
    tgcValidating=true;
    try {
      // Always re-validate against server before allowing checkout
      const fresh  = await fetchCart();
      const result = await validateCheckout();
      if(!result.valid){ showCheckoutError(result.message); setCheckoutBlocked(true); return; }
      hideCheckoutError();
      const form=btn.closest('form');
      if(form){ try{ form.requestSubmit(btn); }catch{ form.submit(); } }
      else window.location.href='/checkout';
    } finally { tgcValidating=false; }
  }, true);

  /* ─── Init ───────────────────────────────────────────────────────────── */
  (async () => {
    // Mark as mutation so the first reconcile ALWAYS resolves the button state
    // (wasMutation=true forces setCheckoutBlocked to run even on an unchanged cart).
    // Without this, a valid cart on page load leaves checkout blocked forever.
    mutationPending = true;
    setCheckoutBlocked(true);

    // Fetch cart + collections in parallel — on first load saves ~300–500 ms;
    // on return visits collections come from sessionStorage so it's near-instant.
    const [cart] = await Promise.all([fetchCart(), loadCollections()]);
    buildFreeTierVariantSet(); // must run after loadCollections populates tierProducts

    // If the page was reached via a /cart/VARIANT:QTY or /cart/add?id= shortcut URL,
    // Shopify already added the item server-side before JS ran. Force an immediate
    // reconcile (mutationPending is already true from init) so the rogue guard fires
    // right away instead of waiting for the next poll.
    if (/\/cart(\/add)?/.test(window.location.pathname)) mutationPending = true;

    // Pass pre-fetched cart so reconcile skips its own /cart.js round-trip.
    await reconcile(cart);
    // 30-second safety net. Real mutations are caught instantly by the fetch/XHR
    // interceptors, so this poll only exists to catch edge cases (e.g. server-side
    // cart changes, browser extensions). Longer interval = no visible flicker.
    setInterval(() => { if(!isReconciling) reconcile(); }, 30000);
  })();
})();
